import { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Save, Settings, Globe, DollarSign, Calendar, Building, Check, RefreshCw, Lock, Eye, EyeOff, Trash2, ShieldCheck, Hash, ToggleLeft, ToggleRight, Mail, Shield, AlertCircle, CheckCircle2, MessageCircle } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import WhatsAppSettingsTab from './WhatsAppSettingsTab';

// Force Vercel rebuild - v2

// ── Password Gate ─────────────────────────────────────────────────────────────
function PasswordGate({ onUnlock }) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleVerify = async () => {
    if (!password.trim()) return toast.error('Please enter your password');
    setLoading(true);
    try {
      // Verify by attempting login with same username
      await api.post('/auth/login', { username: user.username, password });
      onUnlock();
    } catch {
      toast.error('Incorrect password! Settings access denied.', { duration: 3000 });
      setPassword('');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Settings Access</h2>
          <p className="text-sm text-gray-500 mt-1">Enter your admin password</p>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              placeholder="Admin password"
              autoFocus
              className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-4 py-3 text-sm focus:outline-none pr-12"
            />
            <button onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button onClick={handleVerify} disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl font-bold text-sm disabled:opacity-50 transition-colors">
            {loading ? 'Verifying...' : '🔓 Unlock Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '13/03/2026' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY', example: '13-03-2026' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY', example: '13 Mar 2026' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '03/13/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-03-13' },
  { value: 'DD/MM/YY',   label: 'DD/MM/YY',   example: '13/03/26' },
];

const CURRENCIES = [
  { symbol: '₹', name: 'INR', label: 'Indian Rupee (₹)' },
  { symbol: '$', name: 'USD', label: 'US Dollar ($)' },
  { symbol: '€', name: 'EUR', label: 'Euro (€)' },
  { symbol: '£', name: 'GBP', label: 'British Pound (£)' },
  { symbol: 'AED', name: 'AED', label: 'UAE Dirham (AED)' },
  { symbol: '¥', name: 'JPY', label: 'Japanese Yen (¥)' },
];


// ── Password input with show/hide toggle ─────────────────────────────────────
function SystemPasswordInput({ value, onChange, placeholder = 'Password' }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="field-input w-full pr-10"
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ── Email Settings Tab Component ──────────────────────────────────────────────
function EmailSettingsTab() {
  const [config, setConfig] = useState({
    smtp_enabled: 'false',
    smtp_email: '',
    smtp_password: '',
    smtp_name: '',
    admin_emails: '',
    system_email: 'Mojiinnovators@gmail.com',
    resend_api_key: '',
  });
  const [workers, setWorkers] = useState([]);
  const [workerEmails, setWorkerEmails] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // ── Admin Recovery Email ─────────────────────────────────────────────────
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [currentRecoveryEmail, setCurrentRecoveryEmail] = useState('');
  const [savingRecovery, setSavingRecovery] = useState(false);

  useEffect(() => {
    // Load existing email settings
    api.get('/settings').then(r => {
      const s = r.data;
      setConfig({
        smtp_enabled: s.smtp_enabled || 'false',
        smtp_email: s.smtp_email || '',
        smtp_password: s.smtp_password || '',
        smtp_name: s.smtp_name || '',
        admin_emails: s.admin_emails || '',
        system_email: s.system_email || 'Mojiinnovators@gmail.com',
        resend_api_key: s.resend_api_key || '',
      });
      // Load worker emails
      const we = {};
      Object.keys(s).forEach(k => {
        if (k.startsWith('worker_email_')) {
          const id = k.replace('worker_email_', '');
          we[id] = s[k];
        }
      });
      setWorkerEmails(we);
    }).catch(() => {});

    // Load current admin recovery email
    api.get('/auth/my-profile').then(r => {
      const email = r.data?.email || '';
      setCurrentRecoveryEmail(email);
      setRecoveryEmail(email);
    }).catch(() => {});

    // Load workers
    api.get('/workers').then(r => {
      setWorkers(r.data.filter(w => w.is_active));
    }).catch(() => {});
  }, []);

  const saveRecoveryEmail = async () => {
    if (!recoveryEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recoveryEmail.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSavingRecovery(true);
    try {
      await api.put('/auth/update-my-email', { email: recoveryEmail.trim() });
      setCurrentRecoveryEmail(recoveryEmail.trim());
      toast.success('Recovery email saved! ✅');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save recovery email.');
    } finally {
      setSavingRecovery(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Save email config
      const allSettings = { ...config };
      // Save worker emails
      Object.entries(workerEmails).forEach(([id, email]) => {
        allSettings[`worker_email_${id}`] = email;
      });
      await api.put('/settings', allSettings);
      toast.success('Email settings saved! ✅');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      await api.post('/settings/test-email');
      toast.success('Test email sent! Check your inbox ✅');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Test failed — check SMTP config');
    }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-5">

      {/* Admin Recovery Email — for Forgot Password OTP */}
      <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Account Recovery Email</h2>
            <p className="text-xs text-gray-500">OTP for Forgot Password / Forgot Username will be sent to this address</p>
          </div>
        </div>

        {currentRecoveryEmail && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
            <p className="text-sm text-green-800">
              Current recovery email: <span className="font-semibold">{currentRecoveryEmail}</span>
            </p>
          </div>
        )}

        {!currentRecoveryEmail && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">No recovery email set. Forgot Password will not work until you add one.</p>
          </div>
        )}

        <label className="block text-sm font-medium text-gray-700 mb-1">
          {currentRecoveryEmail ? 'Update Recovery Email' : 'Set Recovery Email'}
        </label>
        <div className="flex gap-2">
          <input
            type="email"
            value={recoveryEmail}
            onChange={e => setRecoveryEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <button
            onClick={saveRecoveryEmail}
            disabled={savingRecovery || !recoveryEmail.trim()}
            className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition-colors"
          >
            {savingRecovery ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* System Email Credentials — used to send OTP */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-700 mb-1">OTP Sender — Resend API</p>
          <p className="text-xs text-gray-400 mb-4">
            OTP emails are sent via Resend (free — 3000 emails/month). Gmail SMTP does not work on Railway hosting.
            <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline ml-1">Create free account at resend.com →</a>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sender Email Address</label>
              <input
                type="email"
                value={config.system_email}
                onChange={e => setConfig(c => ({ ...c, system_email: e.target.value }))}
                placeholder="Mojiinnovators@gmail.com"
                className="field-input w-full"
              />
              <p className="text-xs text-gray-400 mt-1">Must be verified in your Resend account</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Resend API Key</label>
              <SystemPasswordInput
                value={config.resend_api_key}
                onChange={v => setConfig(c => ({ ...c, resend_api_key: v }))}
                placeholder="re_xxxxxxxxxxxx"
              />
            </div>
          </div>
          <p className="text-xs text-amber-600 mt-2 font-medium">
            Click "Save Email Settings" button below after filling these fields.
          </p>
        </div>
      </div>

      {/* SMTP Config */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Gmail SMTP Setup</h2>
            <p className="text-xs text-gray-500">Use Gmail to send emails from your account</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-600">Enable Email</span>
            <button
              onClick={() => setConfig(c => ({ ...c, smtp_enabled: c.smtp_enabled === 'true' ? 'false' : 'true' }))}
              className={`w-12 h-6 rounded-full transition-colors ${config.smtp_enabled === 'true' ? 'bg-blue-500' : 'bg-gray-200'}`}
            >
              <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform mt-0.5 ${config.smtp_enabled === 'true' ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gmail Address <span className="text-red-500">*</span></label>
            <input
              value={config.smtp_email}
              onChange={e => setConfig(c => ({ ...c, smtp_email: e.target.value }))}
              placeholder="yourname@gmail.com"
              type="email"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Emails will be sent FROM this address</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gmail App Password <span className="text-red-500">*</span></label>
            <input
              value={config.smtp_password}
              onChange={e => setConfig(c => ({ ...c, smtp_password: e.target.value }))}
              placeholder="xxxx xxxx xxxx xxxx"
              type="password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Google Account → Security → App Passwords</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sender Name</label>
            <input
              value={config.smtp_name}
              onChange={e => setConfig(c => ({ ...c, smtp_name: e.target.value }))}
              placeholder="MOZI INNOVATORS LLP"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email(s) <span className="text-red-500">*</span></label>
            <input
              value={config.admin_emails}
              onChange={e => setConfig(c => ({ ...c, admin_emails: e.target.value }))}
              placeholder="admin@example.com, admin2@example.com"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Comma separated — admin will get task update emails here</p>
          </div>
        </div>

        {/* Gmail App Password Guide */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-800 mb-2">📋 Gmail App Password Kaise Banaye:</p>
          <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
            <li>Google Account → Security tab open karo</li>
            <li>2-Step Verification enable karo (agar nahi hai)</li>
            <li>Search karo "App passwords"</li>
            <li>"Mail" select karo → Generate karo</li>
            <li>16-digit password yahan paste karo</li>
          </ol>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={testEmail} disabled={testing || config.smtp_enabled !== 'true'}
            className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-600 rounded-xl text-sm font-medium hover:bg-blue-50 disabled:opacity-40 transition-colors">
            {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send Test Email
          </button>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Email Settings
          </button>
        </div>
      </div>

      {/* Worker Emails */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="font-bold text-gray-900 mb-1">Worker Email Addresses</h2>
        <p className="text-xs text-gray-500 mb-4">Workers will receive task assignment emails here</p>
        <div className="space-y-3">
          {workers.length === 0 && <p className="text-sm text-gray-400">No active workers found</p>}
          {workers.map(w => (
            <div key={w.id} className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {w.name.charAt(0)}
              </div>
              <span className="text-sm font-medium text-gray-800 w-36 flex-shrink-0 truncate">{w.name}</span>
              <input
                value={workerEmails[w.id] || ''}
                onChange={e => setWorkerEmails(prev => ({ ...prev, [w.id]: e.target.value }))}
                placeholder={`${w.name.toLowerCase().replace(' ', '.')}@email.com`}
                type="email"
                className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          ))}
        </div>
        {workers.length > 0 && (
          <button onClick={save} disabled={saving}
            className="mt-4 flex items-center gap-2 px-5 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 disabled:opacity-40 transition-colors">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Worker Emails
          </button>
        )}
      </div>

      {/* What triggers emails */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
        <p className="text-sm font-semibold text-gray-700 mb-3">📧 Email Triggers</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { who: '👷 Worker', when: 'New task assigned → Worker ko email' },
            { who: '👨‍💼 Admin', when: 'Task progress update → Admin ko email' },
            { who: '👨‍💼 Admin', when: 'Task completed → Admin ko email' },
            { who: '👨‍💼 Admin', when: 'Item all stages done → Admin ko email' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2 bg-white rounded-xl p-3 border border-gray-100">
              <span className="text-xs font-semibold text-gray-500 w-24 flex-shrink-0">{item.who}</span>
              <span className="text-xs text-gray-700">{item.when}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('settings');
  const { settings: globalSettings, setSettings: setGlobalSettings } = useSettings();
  const [unlocked, setUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // General Settings
  const [form, setForm] = useState({
    currency_symbol: '₹',
    currency_name: 'INR',
    date_format: 'DD/MM/YYYY',
    company_name: 'MOZI INNOVATORS LLP',
    company_address: '',
    company_phone: '',
    company_gstin: '',
    company_email: '',
    timezone: 'Asia/Kolkata',
    project_id_prefix: 'WM',
    project_id_start_number: '1001',
    proto_code_prefix: 'PROTO_',
    proto_code_start_number: '1001',
    cft_prefix: 'CFT',
    cft_start_number: '1',
    cft_allow_negative_stock: 'no',
    bank_account_name: '', bank_name: '', bank_account_no: '', bank_ifsc: '', bank_branch: '', bank_account_type: '', bank_upi: '',
    bank_show_on_quotation: 'yes',
    packing_box_prefix: 'BOX',
    packing_mode: 'manual',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirm: false });
  const [pwSaving, setPwSaving] = useState(false);
  const [deletingAllData, setDeletingAllData] = useState(false);

  // Numbering settings state
  const DOC_TYPES = [
    { key: 'po',       label: 'Purchase Order',   defaultPrefix: 'PO',  color: 'blue' },
    { key: 'sale',     label: 'Sale Invoice',      defaultPrefix: 'INV', color: 'purple' },
    { key: 'delivery', label: 'Delivery Challan',  defaultPrefix: 'DC',  color: 'teal' },
  ];
  const [numbering, setNumbering] = useState({
    po:       { mode: 'auto', prefix: 'PO',  start_number: '1001', last_used: null, next_number: null },
    sale:     { mode: 'auto', prefix: 'INV', start_number: '1001', last_used: null, next_number: null },
    delivery: { mode: 'auto', prefix: 'DC',  start_number: '1001', last_used: null, next_number: null },
  });
  const [numberingSaving, setNumberingSaving] = useState(false);

  const loadNumberingInfo = () => {
    api.get('/numbering-info').then(r => setNumbering(r.data)).catch(() => {});
  };

  const setNField = (docType, field, value) =>
    setNumbering(n => ({ ...n, [docType]: { ...n[docType], [field]: value } }));

  const saveNumbering = async () => {
    setNumberingSaving(true);
    try {
      const payload = {};
      for (const { key } of DOC_TYPES) {
        payload[`${key}_numbering_mode`]  = numbering[key].mode;
        payload[`${key}_prefix`]          = numbering[key].prefix;
        payload[`${key}_start_number`]    = numbering[key].start_number;
      }
      await api.put('/settings', payload);
      toast.success('Numbering settings saved ✅');
      loadNumberingInfo(); // refresh next_number preview
    } catch { toast.error('Failed to save numbering settings'); }
    finally { setNumberingSaving(false); }
  };

  // Next CFT sheet number from the server (continues after numbers already used)
  const [cftNext, setCftNext] = useState('');
  const loadCftNext = () => api.get('/cft-next-number').then(r => setCftNext(r.data.next_sheet_no || '')).catch(() => {});

  useEffect(() => {
    api.get('/settings').then(r => {
      setForm(f => ({ ...f, ...r.data }));
    }).catch(() => {});
    loadNumberingInfo();
    loadCftNext();
  }, []);

  const handleDeleteAllData = async () => {
    if (!window.confirm('⚠️ This action is irreversible! All data will be permanently deleted?\n\nAre you absolutely sure?')) return;
    if (!window.confirm('Last chance! All work, workers, projects, tasks - everything will be deleted!\n\nStill want to delete?')) return;
    
    setDeletingAllData(true);
    try {
      await api.post('/admin/delete-all-data');
      toast.success('✅ All data deleted successfully! Database cleared.');
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingAllData(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', form);
      setGlobalSettings(s => ({ ...s, ...form }));
      loadCftNext();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success('Settings saved! ✅');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const selectedCurrency = CURRENCIES.find(c => c.name === form.currency_name) || CURRENCIES[0];
  const selectedDateFormat = DATE_FORMATS.find(d => d.value === form.date_format) || DATE_FORMATS[0];

  const handlePasswordChange = async () => {
    if (!pwForm.current) return toast.error('Please enter current password');
    if (!pwForm.newPw || pwForm.newPw.length < 4) return toast.error('New password must be at least 4 characters');
    if (pwForm.newPw !== pwForm.confirm) return toast.error('New password and confirm password do not match');
    setPwSaving(true);
    try {
      await api.put('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Password changed! ✅');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password change failed');
    } finally { setPwSaving(false); }
  };

  // Show password gate if not unlocked
  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Settings className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-sm">App configuration</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto whitespace-nowrap">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-all ${
            activeTab === 'general'
              ? 'border-amber-500 text-amber-600 bg-amber-50'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Settings className="w-4 h-4" /> General
        </button>
        <button
          onClick={() => setActiveTab('data')}
          className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-all ${
            activeTab === 'data'
              ? 'border-red-500 text-red-600 bg-red-50'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Trash2 className="w-4 h-4" /> Data Management
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-all ${
            activeTab === 'email'
              ? 'border-blue-500 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Mail className="w-4 h-4" /> Email Setup
        </button>
        <button
          onClick={() => setActiveTab('whatsapp')}
          className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-all ${
            activeTab === 'whatsapp'
              ? 'border-green-500 text-green-600 bg-green-50'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp Alerts
        </button>
      </div>

      {/* GENERAL TAB */}
      {activeTab === 'general' && (

      <div className="space-y-5">
        {/* Company Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Company Info</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company / Workshop Name</label>
            <input value={form.company_name}
              onChange={e => setForm({ ...form, company_name: e.target.value })}
              placeholder="e.g. Laxman Furniture Works"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <p className="text-xs text-gray-400 mt-1">This name will appear in reports and the dashboard</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
            <input value={form.company_address || ''}
              onChange={e => setForm({ ...form, company_address: e.target.value })}
              placeholder="e.g. Daulatpura Didwana - 341303"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <p className="text-xs text-gray-400 mt-1">Will appear on packing reports</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
            <input value={form.company_phone || ''}
              onChange={e => setForm({ ...form, company_phone: e.target.value })}
              placeholder="e.g. +91 98765 43210"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <p className="text-xs text-gray-400 mt-1">Will appear on Purchase Orders & Challans</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
            <input value={form.company_gstin || ''}
              onChange={e => setForm({ ...form, company_gstin: e.target.value })}
              placeholder="e.g. 08AABCU9603R1ZX"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <p className="text-xs text-gray-400 mt-1">Will appear on Purchase Orders & Challans</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Email</label>
            <input value={form.company_email || ''}
              onChange={e => setForm({ ...form, company_email: e.target.value })}
              placeholder="e.g. info@yourworkshop.com"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
        </div>

        {/* Bank Details (printed on quotations) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Bank Details</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
              <input value={form.bank_account_name || ''} maxLength={120}
                onChange={e => setForm({ ...form, bank_account_name: e.target.value })}
                placeholder="e.g. MOZI INNOVATORS LLP"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <input value={form.bank_name || ''} maxLength={120}
                onChange={e => setForm({ ...form, bank_name: e.target.value })}
                placeholder="e.g. HDFC Bank"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <input value={form.bank_account_no || ''} maxLength={40}
                onChange={e => setForm({ ...form, bank_account_no: e.target.value })}
                placeholder="e.g. 50200012345678"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
              <input value={form.bank_ifsc || ''} maxLength={20}
                onChange={e => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                placeholder="e.g. HDFC0001234"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
              <input value={form.bank_branch || ''} maxLength={160}
                onChange={e => setForm({ ...form, bank_branch: e.target.value })}
                placeholder="e.g. Vaishali Nagar, Jaipur"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID (optional)</label>
              <input value={form.bank_upi || ''} maxLength={80}
                onChange={e => setForm({ ...form, bank_upi: e.target.value })}
                placeholder="e.g. mozi@hdfcbank"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
              <select value={form.bank_account_type || ''} onChange={e => setForm({ ...form, bank_account_type: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
                <option value="">—</option><option value="Current">Current</option><option value="Savings">Savings</option><option value="Cash Credit">Cash Credit</option><option value="Overdraft">Overdraft</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
            <input type="checkbox" className="w-4 h-4 accent-amber-500" checked={(form.bank_show_on_quotation || 'yes') === 'yes'}
              onChange={e => setForm({ ...form, bank_show_on_quotation: e.target.checked ? 'yes' : 'no' })} />
            Print these bank details on quotations
          </label>
          <p className="text-xs text-gray-400 mt-1">They print beside the totals, under the amount in words. Click Save All Settings to apply.</p>
        </div>

        {/* Currency Settings */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Currency</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CURRENCIES.map(c => (
              <button key={c.name} onClick={() => setForm({ ...form, currency_symbol: c.symbol, currency_name: c.name })}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                  form.currency_name === c.name
                    ? 'border-amber-400 bg-amber-50'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                }`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${
                  form.currency_name === c.name ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {c.symbol.length > 1 ? <span className="text-xs">{c.symbol}</span> : c.symbol}
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">{c.label}</p>
                  <p className="text-xs text-gray-400">Example: {c.symbol}10,000</p>
                </div>
                {form.currency_name === c.name && <Check className="w-4 h-4 text-amber-500 ml-auto" />}
              </button>
            ))}
          </div>

          {/* Custom currency override */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Currency Symbol</label>
            <input value={form.currency_symbol}
              onChange={e => setForm({ ...form, currency_symbol: e.target.value })}
              placeholder="₹"
              className="w-32 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
            <p className="text-xs text-gray-400 mt-1">Select from above or type your own custom symbol</p>
          </div>

          {/* Preview */}
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
            <span className="text-sm text-gray-600">Preview:</span>
            <span className="text-lg font-bold text-amber-700">{form.currency_symbol}99,999</span>
            <span className="text-xs text-gray-400">· {form.currency_symbol}1,50,000 · {form.currency_symbol}25,000</span>
          </div>
        </div>

        {/* Date Format */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Date Format</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {DATE_FORMATS.map(df => (
              <button key={df.value} onClick={() => setForm({ ...form, date_format: df.value })}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  form.date_format === df.value
                    ? 'border-amber-400 bg-amber-50'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                }`}>
                <p className={`font-semibold text-sm ${form.date_format === df.value ? 'text-amber-700' : 'text-gray-700'}`}>
                  {df.label}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{df.example}</p>
                {form.date_format === df.value && (
                  <div className="mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3 text-amber-500" />
                    <span className="text-xs text-amber-600 font-medium">Selected</span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Preview */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
            <span className="text-sm text-gray-600">Today's date:</span>
            <span className="text-base font-bold text-blue-700">
              {(() => {
                const d = new Date();
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                const yy = String(yyyy).slice(-2);
                const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const mmm = months[d.getMonth()];
                switch (form.date_format) {
                  case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
                  case 'DD-MM-YYYY': return `${dd}-${mm}-${yyyy}`;
                  case 'DD MMM YYYY': return `${dd} ${mmm} ${yyyy}`;
                  case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
                  case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
                  case 'DD/MM/YY': return `${dd}/${mm}/${yy}`;
                  default: return `${dd}/${mm}/${yyyy}`;
                }
              })()}
            </span>
          </div>
        </div>

        {/* Project ID Format */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Project ID Format</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project ID Prefix</label>
              <input value={form.project_id_prefix || 'WM'}
                onChange={e => setForm({ ...form, project_id_prefix: e.target.value })}
                placeholder="e.g. WM, PRJ, MFW"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">What to prefix at the start of Project IDs (e.g. WM, PRJ, MFW)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starting Number</label>
              <input type="number" value={form.project_id_start_number || '1001'}
                onChange={e => setForm({ ...form, project_id_start_number: e.target.value })}
                placeholder="1001"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">What number should the first project ID start with</p>
            </div>

            {/* Preview */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
              <p className="text-sm text-gray-600 mb-1">Preview:</p>
              <p className="text-base font-bold text-purple-700">{form.project_id_prefix}-26-{String(form.project_id_start_number || '1001').padStart(4, '0')}</p>
              <p className="text-xs text-gray-400 mt-1">Next project ID will look like this (year will be current, number will increment)</p>
            </div>
          </div>
        </div>

        {/* Proto Code Series (used by Manufacturing Orders in Automatic Proto Code mode) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Proto Code Series</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proto Code Prefix</label>
              <input value={form.proto_code_prefix ?? 'PROTO_'} maxLength={40}
                onChange={e => setForm({ ...form, proto_code_prefix: e.target.value })}
                placeholder="e.g. PROTO_"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">Written before the number, e.g. PROTO_ gives PROTO_1001</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starting Number</label>
              <input type="number" min="1" value={form.proto_code_start_number || '1001'}
                onChange={e => setForm({ ...form, proto_code_start_number: e.target.value })}
                placeholder="1001"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">Codes start from this number. Numbers already used anywhere are skipped, so codes never repeat</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
              <p className="text-sm text-gray-600 mb-1">Preview:</p>
              <p className="text-base font-bold text-purple-700">{(form.proto_code_prefix ?? 'PROTO_') || 'PROTO_'}{form.proto_code_start_number || '1001'}</p>
              <p className="text-xs text-gray-400 mt-1">Used only by Manufacturing Orders created in <b>Automatic Proto Code</b> mode. Manual orders keep typed codes.</p>
            </div>
          </div>
        </div>

        {/* CFT Sheet Number Series (new CFT sheets take the next number automatically) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Hash className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">CFT Sheet Number Series</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CFT Sheet Prefix</label>
              <input value={form.cft_prefix ?? 'CFT'} maxLength={20}
                onChange={e => setForm({ ...form, cft_prefix: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                placeholder="e.g. CFT"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">Written before the year and number, e.g. CFT gives CFT-{new Date().getFullYear()}-0001</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starting Number</label>
              <input type="number" min="1" value={form.cft_start_number || '1'}
                onChange={e => setForm({ ...form, cft_start_number: e.target.value })}
                placeholder="1"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">Set once. Every new CFT sheet takes the next number automatically. Numbers already used are skipped, so a number never repeats.</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
              <p className="text-sm text-gray-600 mb-1">Format:</p>
              <p className="text-base font-bold text-purple-700">{(form.cft_prefix || 'CFT')}-{new Date().getFullYear()}-{String(Math.max(1, parseInt(form.cft_start_number, 10) || 1)).padStart(4, '0')}</p>
              {cftNext && <p className="text-xs text-gray-500 mt-1">Next CFT sheet will be <b className="text-purple-700">{cftNext}</b> (after saving changes, this updates).</p>}
            </div>
          </div>
        </div>

        {/* CFT wood stock rule */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">CFT Wood Stock</h2>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Allow negative wood stock?</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'no', label: 'No — stop it', desc: 'A CFT sheet, transfer or inward change that takes any wood thickness below zero is refused.' },
              { value: 'yes', label: 'Yes — allow it', desc: 'Stock may go below zero. A red warning is still shown on the sheet and in Approvals.' },
            ].map(opt => (
              <button key={opt.value} type="button"
                onClick={() => setForm({ ...form, cft_allow_negative_stock: opt.value })}
                className={`border-2 rounded-xl p-3 text-left transition-all ${(form.cft_allow_negative_stock || 'no') === opt.value ? (opt.value === 'no' ? 'border-green-500 bg-green-50' : 'border-amber-400 bg-amber-50') : 'border-gray-200 hover:border-gray-300'}`}>
                <p className="font-semibold text-sm text-gray-800">{opt.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">Checked when a CFT sheet is sent for approval, approved or reopened, and when a Wood Inward entry or Transfer is saved or deleted. Click Save All Settings to apply.</p>
        </div>

        {/* Packing Settings */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">📦</span>
            <h2 className="font-semibold text-gray-800">Packing Settings</h2>
          </div>
          <div className="space-y-4">
            {/* Packing Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Packing Mode</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'manual', label: '✋ Manual', desc: 'Worker adds items manually' },
                  { value: 'auto', label: '⚡ Auto', desc: 'Auto-linked from project items' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm({ ...form, packing_mode: opt.value })}
                    className={`border-2 rounded-xl p-3 text-left transition-all ${form.packing_mode === opt.value ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <p className="font-semibold text-sm text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Box Number Prefix */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Box Number Prefix</label>
              <input value={form.packing_box_prefix || 'BOX'}
                onChange={e => setForm({ ...form, packing_box_prefix: e.target.value.toUpperCase() })}
                placeholder="e.g. MJ-25, BOX, PKG"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <p className="text-xs text-gray-400 mt-1">Box numbers will be: {form.packing_box_prefix || 'BOX'}-01, {form.packing_box_prefix || 'BOX'}-02...</p>
            </div>

            {/* Preview */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
              <p className="text-sm text-gray-600 mb-1">Box Number Preview:</p>
              <p className="text-base font-bold text-purple-700">{form.packing_box_prefix || 'BOX'}-01</p>
            </div>
          </div>
        </div>

        {/* Document Numbering Settings */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-gray-600" />
              <h2 className="font-semibold text-gray-800">Document Numbering</h2>
            </div>
            <button onClick={saveNumbering} disabled={numberingSaving}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-60">
              <Save className="w-4 h-4" />
              {numberingSaving ? 'Saving...' : 'Save Numbering'}
            </button>
          </div>

          <div className="space-y-5">
            {DOC_TYPES.map(({ key, label, defaultPrefix, color }) => {
              const n = numbering[key] || {};
              const isAuto = n.mode !== 'manual';
              const colorMap = {
                blue:   { badge: 'bg-blue-100 text-blue-700',   ring: 'focus:ring-blue-400',   btn: 'bg-blue-600 text-white', preview: 'bg-blue-50 border-blue-200 text-blue-800' },
                purple: { badge: 'bg-purple-100 text-purple-700', ring: 'focus:ring-purple-400', btn: 'bg-purple-600 text-white', preview: 'bg-purple-50 border-purple-200 text-purple-800' },
                teal:   { badge: 'bg-teal-100 text-teal-700',   ring: 'focus:ring-teal-400',   btn: 'bg-teal-600 text-white', preview: 'bg-teal-50 border-teal-200 text-teal-800' },
                orange: { badge: 'bg-orange-100 text-orange-700', ring: 'focus:ring-orange-400', btn: 'bg-orange-600 text-white', preview: 'bg-orange-50 border-orange-200 text-orange-800' },
              };
              const c = colorMap[color];

              return (
                <div key={key} className="border border-gray-200 rounded-xl p-4 space-y-4">
                  {/* Doc type header + mode toggle */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${c.badge}`}>{label}</span>
                    </div>
                    {/* Mode Toggle */}
                    <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                      <button onClick={() => setNField(key, 'mode', 'auto')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isAuto ? c.btn + ' shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <ToggleRight className="w-3.5 h-3.5" /> Auto Series
                      </button>
                      <button onClick={() => setNField(key, 'mode', 'manual')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${!isAuto ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <ToggleLeft className="w-3.5 h-3.5" /> Manual
                      </button>
                    </div>
                  </div>

                  {isAuto ? (
                    /* AUTO MODE */
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Prefix</label>
                        <input value={n.prefix || defaultPrefix}
                          onChange={e => setNField(key, 'prefix', e.target.value.toUpperCase())}
                          placeholder={defaultPrefix}
                          className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 ${c.ring}`} />
                        <p className="text-xs text-gray-400 mt-1">Letters that appear before the number</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Starting Number</label>
                        <input type="number" value={n.start_number || '1001'}
                          onChange={e => setNField(key, 'start_number', e.target.value)}
                          placeholder="1001"
                          className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${c.ring}`} />
                        <p className="text-xs text-gray-400 mt-1">First number in the series</p>
                      </div>
                      {/* Preview */}
                      <div className={`sm:col-span-2 border rounded-xl p-3 ${c.preview}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium opacity-70 mb-1">Next number will be:</p>
                            <p className="text-lg font-bold font-mono">
                              {n.next_number || `${(n.prefix || defaultPrefix)}-${String(n.start_number || '1001').padStart(4, '0')}`}
                            </p>
                          </div>
                          {n.last_used && (
                            <div className="text-right">
                              <p className="text-xs font-medium opacity-70 mb-1">Last used:</p>
                              <p className="text-sm font-semibold font-mono opacity-80">{n.last_used}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* MANUAL MODE */
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <span className="text-amber-500 text-base mt-0.5">✍️</span>
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Manual Mode</p>
                          <p className="text-xs text-amber-700 mt-0.5">You will enter the {label} number yourself each time you create a document.</p>
                        </div>
                      </div>
                      {n.last_used ? (
                        <div className={`border rounded-xl p-3 ${c.preview}`}>
                          <p className="text-xs font-medium opacity-70 mb-1">Last used number:</p>
                          <p className="text-lg font-bold font-mono">{n.last_used}</p>
                          <p className="text-xs opacity-60 mt-1">You can continue from here or use any number you prefer</p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                          <p className="text-xs text-gray-500">No {label} created yet — you will set the number when creating the first one.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Admin Password Change */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-800">Admin Password Change</h2>
          </div>
          <div className="space-y-3 max-w-sm">
            {[
              ['current', 'Current Password *', 'Enter current password'],
              ['newPw', 'New Password *', 'At least 4 characters'],
              ['confirm', 'Confirm New Password *', 'Repeat new password']
            ].map(([field, label, placeholder]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <div className="relative">
                  <input
                    type={showPw[field] ? 'text' : 'password'}
                    value={pwForm[field]}
                    onChange={e => setPwForm({ ...pwForm, [field]: e.target.value })}
                    placeholder={placeholder}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <button type="button" onClick={() => setShowPw(p => ({ ...p, [field]: !p[field] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPw[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
            {pwForm.newPw && pwForm.confirm && pwForm.newPw !== pwForm.confirm && (
              <p className="text-xs text-red-500">⚠️ Passwords do not match</p>
            )}
            {pwForm.newPw && pwForm.confirm && pwForm.newPw === pwForm.confirm && pwForm.confirm.length >= 4 && (
              <p className="text-xs text-green-600">✅ Passwords match</p>
            )}
            <button onClick={handlePasswordChange} disabled={pwSaving}
              className="w-full bg-slate-700 hover:bg-slate-800 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 mt-2">
              {pwSaving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Changing...</> : <><Lock className="w-4 h-4" /> Change Password</>}
            </button>
          </div>
        </div>

        {/* Save Button Bottom */}
        <div className="flex justify-end pb-4">
          <button onClick={handleSave} disabled={saving || !canEdit}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all shadow-sm ${
              !canEdit ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : saved ? 'bg-green-500 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}>
            {saved ? <><Check className="w-4 h-4" /> Saved!</> : saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save All Settings</>}
          </button>
        </div>
      </div>
      )}

      {/* BACKUP TAB DISABLED - System no longer uses backup/restore */}

      {/* DATA MANAGEMENT TAB */}
      {activeTab === 'email' && (
        <EmailSettingsTab />
      )}
      {activeTab === 'whatsapp' && (
        <WhatsAppSettingsTab />
      )}
      {activeTab === 'data' && (
        <div className="space-y-5">
          {/* Delete All Data */}
          <div className="bg-red-50 border border-red-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-red-900 text-lg mb-2">Delete All Data</h2>
                <p className="text-sm text-red-700 mb-4">
                  ⚠️ <strong>This action is permanent!</strong> All projects, tasks, workers, images, time logs - <strong>everything</strong> will be deleted. Only do this after taking a backup.
                </p>
                <div className="space-y-2 text-sm text-red-700 mb-4">
                  <p>✓ All projects and tasks will be deleted</p>
                  <p>✓ All worker data and time logs will be deleted</p>
                  <p>✓ Uploaded images will be deleted</p>
                  <p>✓ Admin and worker accounts will remain active, settings will remain</p>
                </div>
                <button
                  onClick={handleDeleteAllData}
                  disabled={deletingAllData}
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold disabled:opacity-50 transition-all"
                >
                  {deletingAllData ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                      Deleting...
                    </>
                  ) : (
                    '🗑️ Delete All Data'
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">💡 Tip:</span> Take a backup first, then delete. If deleted by mistake, you can restore from backup.
            </p>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
